// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import type { ConnectionStatus } from '@capacitor/network';
import { Network } from '@capacitor/network';
import { environment } from 'src/environments/environment';
import { LoggerService } from './logger.service';

/**
 * THE connectivity authority for the app — pure detection/state, no offline
 * *policy*. Everything connectivity-aware (the offline banner, the HTTP
 * interceptor's fail-fast) reads `isOnline` from here, so the planned offline
 * mode extends one place rather than chasing scattered `navigator.onLine` checks.
 *
 * `isOnline` means "the app is reachable", not "a network exists" (EF-1.1):
 *
 *   isOnline = device link (@capacitor/network)
 *              AND no standing evidence that the server is unreachable.
 *
 * The DEVICE side is push-based: native reachability events on iOS, the
 * plugin's web fallback otherwise. Seeded synchronously from `navigator.onLine`
 * so it has a value from first paint; the plugin's `getStatus()` corrects it,
 * and `networkStatusChange` keeps it live. The listener is registered BEFORE
 * the `getStatus()` call so a flip landing between the two can't be missed.
 *
 * The SERVER side is passive evidence fed by httpHandlerInterceptor:
 * transport-level failures (status 0, request timeout) of requests that
 * actually went out mark the server unreachable; any answer from the server —
 * success or HTTP error — clears it, and so does a fresh device link
 * (optimistic reset). No active probe on the SERVER layer: the first real
 * request refreshes the evidence. The DEVICE layer does keep one safety net —
 * a slow recovery poll that runs only while the link reads disconnected,
 * because the path monitor can wedge (simulator) or miss events (see the
 * constructor). Semantics are unchanged in spirit: online is a promise,
 * offline is a fact.
 *
 * Product decided on TWO user-facing states (addendum v0.3 §8.1): a working
 * network with an unreachable app is simply "offline" here — no third state.
 */
@Injectable({ providedIn: 'root' })
export class NetworkService {
  private readonly logger = inject(LoggerService);
  private readonly _deviceConnected = signal(this.readOnline());
  private readonly _serverUnreachable = signal(false);
  private readonly _lastServerContactAt = signal<number | null>(null);
  private recoveryPoll: ReturnType<typeof setInterval> | null = null;

  /** Reachability of the app, not mere connectivity. Optimistic when true (see class note). */
  readonly isOnline = computed(() => this._deviceConnected() && !this._serverUnreachable());

  /**
   * The raw device link, without the server-evidence layer. For the HTTP
   * handler's fail-fast ONLY: with no device link nothing can succeed, so
   * failing fast is safe — but while the device is online and only the SERVER
   * is marked unreachable, requests must still go out: they are the probes
   * that clear the evidence. Gating the fail-fast on `isOnline` would trap the
   * app offline until a device event or a reload (found by bed pass,
   * 2026-08-18). UI reads `isOnline`, never this.
   */
  readonly deviceConnected = this._deviceConnected.asReadonly();

  /**
   * Epoch ms of the last answer received from the server (any status) — the
   * honest precursor of EF-1.2's "last successful sync" until the sync engine
   * exists; the status indicator displays it. Null until the first answer of
   * the session.
   */
  readonly lastServerContactAt = this._lastServerContactAt.asReadonly();

  constructor() {
    // Root singleton for the app's lifetime — the listener intentionally persists.
    void Network.addListener('networkStatusChange', (status) => this.apply(status));
    void Network.getStatus().then((status) => this.apply(status));

    // Missed-event recovery: the change events are the fast path, but the
    // simulator's path monitor can WEDGE after a host Wi-Fi toggle — events
    // AND getStatus both keep reporting the stale state (proven in bed,
    // 2026-08-19), and a stranded deviceConnected=false means the handler
    // fail-fasts everything: no traffic could ever prove the network came
    // back. While disconnected, poll a source that cannot wedge: the plugin
    // first (right on real devices), then an actual HTTP round-trip — ANY
    // response, even an error status, proves the path. Native only: the web
    // build's browser events are reliable, and a cross-origin HEAD would
    // false-negative there. Stops the moment the link is back.
    effect(() => {
      if (this._deviceConnected()) {
        if (this.recoveryPoll !== null) {
          clearInterval(this.recoveryPoll);
          this.recoveryPoll = null;
        }
      } else if (this.recoveryPoll === null) {
        this.recoveryPoll = setInterval(() => void this.probeConnectivity(), 5_000);
      }
    });
  }

  /**
   * Evidence feed from httpHandlerInterceptor: `false` for transport-level
   * failures (status 0 / timeout) of requests that actually went out, `true`
   * for any response from the server — an answering server is reachable even
   * when it answers with an error.
   */
  reportServerReachable(reachable: boolean): void {
    if (this._serverUnreachable() === reachable) {
      // Transition (rare, high-signal) — logged; steady-state reports are not.
      this.logger.info('Network', reachable ? 'server reachable again' : 'server unreachable (device link up)');
    }
    if (reachable) {
      this._lastServerContactAt.set(Date.now());
      // Positive evidence beats a stale device signal: a real answer arrived,
      // so the path works — whatever the last (possibly missed) event said.
      this._deviceConnected.set(true);
    }
    this._serverUnreachable.set(!reachable);
  }

  private apply(status: ConnectionStatus): void {
    // Transitions only: the recovery poll re-queries every 5s while offline,
    // and a same-state answer is neither news nor worth a log line.
    if (status.connected === this._deviceConnected()) {
      return;
    }
    // Connectivity transitions are rare, high-signal events — logged in every
    // build so field diagnostics of the (planned) offline mode can trace them.
    this.logger.info('Network', 'status change', status);
    if (status.connected) {
      // Fresh device link: clear stale server evidence so the state is
      // optimistic again — the next real request re-evaluates it.
      this._serverUnreachable.set(false);
    }
    this._deviceConnected.set(status.connected);
  }

  private async probeConnectivity(): Promise<void> {
    try {
      const status = await Network.getStatus();
      if (status.connected) {
        this.apply(status);
        return;
      }
    } catch {
      // Plugin unavailable — fall through to the HTTP truth.
    }
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      // no-cors: the gateway root sends no CORS headers, and an opaque response
      // is all the evidence this probe needs.
      await fetch(environment.apiUrl, { method: 'HEAD', cache: 'no-store', mode: 'no-cors' });
      // Reached the gateway: the path works, whatever the wedged monitor says.
      this.reportServerReachable(true);
    } catch {
      // Still down — the next tick retries.
    }
  }

  private readOnline(): boolean {
    return typeof navigator !== 'undefined' && 'onLine' in navigator ? navigator.onLine : true;
  }
}
